/**
=========================================================
* Iris Design Limited  Fulfilment POD - v2.2.0
=========================================================

* Product Page: https://www.irisdesignlimited.com/product/material-dashboard-react
* Copyright 2023 Dev Iris Design Limited Company (https://www.irisdesignlimited.com)

Coded by www.irisdesignlimited.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useRef, useState } from "react";
import "./App.css"
// react-router components
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

// @mui material components
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import { ThemeProvider } from "@mui/material/styles";

// Iris Design Limited  Fulfilment POD components
import MDBox from "components/MDBox";

// Iris Design Limited  Fulfilment POD example components
import Configurator from "examples/Configurator";
import Sidenav from "examples/Sidenav";

// Iris Design Limited  Fulfilment POD themes
import theme from "assets/theme";

// Iris Design Limited  Fulfilment POD Dark Mode themes
import themeDark from "assets/theme-dark";
import { useSelector } from 'react-redux';
// RTL plugins
import createCache from "@emotion/cache";
import { useNavigate } from "react-router-dom";
import rtlPlugin from "stylis-plugin-rtl";
// Iris Design Limited  Fulfilment POD routes
import routes from "routes";
import routesForDesign from "routesForDesign";
import routesBeforeLogin from "routesBeforeLogin";

// Iris Design Limited  Fulfilment POD contexts
import { setMiniSidenav, setOpenConfigurator, useMaterialUIController } from "context";

// Images
import brandDark from "assets/images/logo-ct-dark.png";
import brandWhite from "assets/images/logo-ct.png";
import LoadingScreen from "Loading";
import { canSeeFinancialReport, isFinancialOnlyAccount, isOrdersViewAccount } from "utils/access";

const filterRoutesByAccess = (allRoutes, admin, permission, user) => {
  const financialOnly = isFinancialOnlyAccount(admin, permission, user);
  const ordersView = isOrdersViewAccount(admin, permission);
  return allRoutes
    .filter((route) => {
      if (ordersView) {
        return route.key === "orders" || route.key === "log-out";
      }
      const isFinancialRoute =
        route.key === "financial-report" || route.route === "/financial-report";
      if (isFinancialRoute) {
        return canSeeFinancialReport(admin, permission, user);
      }
      if (financialOnly) return false;
      return !route.adminOnly || admin;
    })
    .map((route) =>
      route.collapse
        ? { ...route, collapse: filterRoutesByAccess(route.collapse, admin, permission, user) }
        : route
    );
};

export default function App() {
  const permission = useSelector((state) => state.user.permission || []);
  const admin = useSelector((state) => state.user.admin === true);
  const user = useSelector((state) => state.user.user);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isLoading = useSelector((state) => state.user.isLoading);
  const financialOnly = isFinancialOnlyAccount(admin, permission, user);
  const ordersView = isOrdersViewAccount(admin, permission);
  const homeRoute = ordersView ? "/orders" : financialOnly ? "/financial-report" : "/dashboard";

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const availableRoutes = useMemo(
    () => filterRoutesByAccess(routes, admin, permission, user),
    [admin, permission, user]
  );
  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const wasLoggedIn = useRef(isLoggedIn);

  useEffect(() => {
    const justLoggedIn = isLoggedIn && !wasLoggedIn.current;
    wasLoggedIn.current = isLoggedIn;

    if (justLoggedIn && pathname.startsWith("/authentication")) {
      navigate(homeRoute);
    }
  }, [homeRoute, isLoggedIn, pathname, navigate]);

  useEffect(() => {
    if (!isLoggedIn || !financialOnly) return;
    if (pathname.startsWith("/authentication") || pathname === "/financial-report") return;
    navigate("/financial-report", { replace: true });
  }, [financialOnly, isLoggedIn, navigate, pathname]);

  useEffect(() => {
    if (!isLoggedIn || !ordersView) return;
    if (pathname.startsWith("/authentication") || pathname === "/orders") return;
    navigate("/orders", { replace: true });
  }, [isLoggedIn, navigate, ordersView, pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    });
  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      {isLoading && <LoadingScreen />}
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="Iris Design Limited Fulfilment POD"
            routes={availableRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {isLoggedIn ? (
          <>
            {/* {permission.includes("design_link") ? getRoutes(routesForDesign) : getRoutes(routes)} */}
            {getRoutes(availableRoutes)}
            <Route path="*" element={<Navigate to={homeRoute} />} />
          </>
        ) : (
          <>
            {getRoutes(routesBeforeLogin)}
            <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
          </>
        )}
      </Routes>
    </ThemeProvider >
  );
}
